import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Article } from "@/custom-types/Article";
import { Brand } from "@/custom-types/Brand";
import { Dispensary } from "@/custom-types/Dispensary";
import { MenuItem } from "@/custom-types/MenuItem";
import { SearchPageTypes } from "@/custom-types/Search";
import { Strain } from "@/custom-types/Strain";
import { HYDRATE } from "@/redux/reducers/hydration";

export const MAX_DISTANCE = 30; // 30 miles
export const DEFAULT_SEARCH_TAKE = 12;
export const DEFAULT_SEARCH_SKIP = 0;

export type SearchState = {
  article: Article[];
  brand: Brand[];
  categoryRelevanceOrder: SearchPageTypes[];
  dispensary: Dispensary[];
  loading: boolean;
  menuItem: MenuItem[];
  menuItemDispensaries: Dispensary[];
  strain: Strain[];
  totalCounts?: {
    article?: number;
    brand?: number;
    dispensary?: number;
    menuItem?: number;
    strain?: number;
  };
};

export type SearchResults = Omit<SearchState, "loading">;

export const initialState: SearchState = {
  article: [],
  brand: [],
  categoryRelevanceOrder: [
    "strain",
    "menuItem",
    "dispensary",
    "brand",
    "article",
  ],
  dispensary: [],
  loading: true,
  menuItem: [],
  menuItemDispensaries: [],
  strain: [],
  totalCounts: {
    article: 0,
    brand: 0,
    dispensary: 0,
    menuItem: 0,
    strain: 0,
  },
};

const searchSlice = createSlice({
  extraReducers: (builder) => {
    /**
     * Ignore any hydration events after initial page load to preserve any
     * subsequent changes that have been made on the client.
     */
    builder.addCase(HYDRATE, () => {});
  },
  initialState,
  name: "search",
  reducers: {
    loadMore: (state, action: PayloadAction<Partial<SearchResults>>) => {
      const { totalCounts, ...rest } = action.payload;

      state.totalCounts = { ...state.totalCounts, ...totalCounts };
      Object.assign(state, rest);
    },
    setLoading: (state) => {
      state.loading = true;
    },
    setResults: (state, action: PayloadAction<SearchResults>) => {
      state.loading = false;
      Object.assign(state, action.payload);
    },
  },
});

export const { setLoading, setResults, loadMore } = searchSlice.actions;

export default searchSlice.reducer;
